import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Check, Loader2, TrendingUp, Users, Link2, BarChart3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

export default function Ambassador() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [amb, setAmb] = useState(null);
  const [state, setState] = useState("idle");
  const [copied, setCopied] = useState(false);

  const [statCode, setStatCode] = useState("");
  const [stats, setStats] = useState(null);
  const [statState, setStatState] = useState("idle");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const link = amb ? `${window.location.origin}/?ref=${amb.code}` : "";

  const generate = async (e) => {
    e.preventDefault();
    setState("loading");
    try {
      const { data } = await axios.post(`${API}/ambassadors`, form);
      setAmb(data);
      setStatCode(data.code);
      setState("done");
    } catch {
      setState("error");
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const checkStats = async (e) => {
    e.preventDefault();
    setStatState("loading");
    try {
      const { data } = await axios.get(`${API}/ambassadors/${statCode.trim().toUpperCase()}/stats`);
      setStats(data);
      setStatState("done");
    } catch {
      setStats(null);
      setStatState("notfound");
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-4xl mx-auto py-12 md:py-16">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)] mb-8">
        <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
      </Link>
      <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-3">
        Espace ambassadeur
      </span>
      <h1 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-[var(--oz-text)]">
        Partagez Ozentya, suivez vos ventes
      </h1>
      <p className="mt-4 text-base text-[var(--oz-muted)] max-w-2xl">
        Générez votre lien personnel et partagez-le à votre entourage. Les commandes passées via votre lien
        sont automatiquement rattachées à votre code — vous pouvez suivre vos ventes ici à tout moment.
      </p>

      <div className="mt-8 grid gap-3 sm:grid-cols-3">
        {[{ icon: Link2, t: "Votre lien unique" }, { icon: Users, t: "Partagez facilement" }, { icon: TrendingUp, t: "Suivez vos ventes" }].map((b, i) => (
          <div key={i} className="bg-white border border-[var(--oz-border)] rounded-2xl p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--oz-nude)] flex items-center justify-center text-[var(--oz-badge)]">
              <b.icon className="w-5 h-5" />
            </div>
            <span className="text-sm font-semibold text-[var(--oz-text)]">{b.t}</span>
          </div>
        ))}
      </div>

      {/* Generate link */}
      <div className="mt-10 bg-white border border-[var(--oz-border)] rounded-[1.5rem] p-6 md:p-8">
        <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-5">1. Obtenir mon lien</h2>
        {!amb ? (
          <form onSubmit={generate} className="grid sm:grid-cols-2 gap-4">
            <input
              data-testid="ambassador-name" required placeholder="Votre nom"
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
            />
            <input
              data-testid="ambassador-email" type="email" required placeholder="Votre email"
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl border border-[var(--oz-border)] px-4 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
            />
            <button
              data-testid="ambassador-generate" disabled={state === "loading"}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors disabled:opacity-60"
            >
              {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Générer mon lien"}
            </button>
            {state === "error" && <p className="sm:col-span-2 text-xs text-[var(--oz-badge)]">Une erreur est survenue. Réessayez.</p>}
          </form>
        ) : (
          <div data-testid="ambassador-result">
            <p className="text-sm text-[var(--oz-muted)]">Votre code : <strong className="text-[var(--oz-text)]">{amb.code}</strong>{amb.existing && " (déjà existant)"}</p>
            <div className="mt-3 flex items-center gap-2 bg-[var(--oz-bg-2)] rounded-xl p-3">
              <span data-testid="ambassador-link" className="flex-1 text-sm text-[var(--oz-text)] break-all">{link}</span>
              <button data-testid="ambassador-copy" onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[var(--oz-dark)] text-white text-xs font-semibold">
                {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="mt-6 bg-white border border-[var(--oz-border)] rounded-[1.5rem] p-6 md:p-8">
        <h2 className="font-serif text-2xl text-[var(--oz-text)] mb-5">2. Suivre mes ventes</h2>
        <form onSubmit={checkStats} className="flex gap-2">
          <input
            data-testid="ambassador-stat-code" placeholder="Votre code (ex. MARIE1A2B)"
            value={statCode} onChange={(e) => setStatCode(e.target.value)}
            className="flex-1 rounded-full border border-[var(--oz-border)] px-5 py-3 text-sm outline-none focus:border-[var(--oz-gold)]"
          />
          <button data-testid="ambassador-stat-submit" disabled={statState === "loading"} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--oz-gold)] text-white font-semibold text-sm hover:bg-[var(--oz-gold-hover)] transition-colors disabled:opacity-60">
            {statState === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />} Voir
          </button>
        </form>

        {statState === "notfound" && <p className="mt-4 text-sm text-[var(--oz-badge)]">Code introuvable.</p>}
        {statState === "done" && stats && (
          <div data-testid="ambassador-stats" className="mt-6 grid grid-cols-2 gap-4">
            <div className="bg-[var(--oz-bg-2)] rounded-2xl p-5 text-center">
              <div className="text-xs uppercase tracking-wider text-[var(--oz-muted)]">Commandes</div>
              <div className="font-serif text-3xl text-[var(--oz-text)] mt-1">{stats.orders}</div>
            </div>
            <div className="bg-[var(--oz-bg-2)] rounded-2xl p-5 text-center">
              <div className="text-xs uppercase tracking-wider text-[var(--oz-muted)]">Ventes générées</div>
              <div className="font-serif text-3xl text-[var(--oz-gold)] mt-1">{fmt(stats.total_sales)}</div>
            </div>
          </div>
        )}
        <p className="mt-4 text-[11px] text-[var(--oz-muted)] italic">
          Le suivi comptabilise les commandes payées passées via votre lien. Aucun revenu n'est garanti ;
          les résultats dépendent des ventes effectivement réalisées.
        </p>
      </div>
    </div>
  );
}
